import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { initNotifications } from '../utils/notifications';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('tinysteps_token');
    const savedUser = localStorage.getItem('tinysteps_user');
    if (token && savedUser) {
      const parsed = JSON.parse(savedUser);
      // Migrate users stored before onboardingComplete was added
      if (parsed.onboardingComplete === undefined) parsed.onboardingComplete = true;
      setUser(parsed);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Refresh FCM token on every app open — covers PWA home screen launch,
      // token rotation by Firebase, and users who never logged out
      initNotifications(parsed.journeyStage)
        .then(r => { if (!r.success && r.reason !== 'permission_denied') console.warn('[FCM] app-open init:', r.reason); })
        .catch(err => console.error('[FCM] app-open init error:', err.message));
    }
    setLoading(false);
  }, []);

  const register = async (name, email, password, dateOfBirth, phone, nickname) => {
    try {
      setError(null);
      const res = await axios.post(`${API_URL}/auth/register`, { name, email, password, dateOfBirth, phone, nickname: nickname?.trim() || '' });
      const { token, ...userData } = res.data;
      const userToStore = { ...userData, onboardingComplete: false };
      localStorage.setItem('tinysteps_token', token);
      localStorage.setItem('tinysteps_user', JSON.stringify(userToStore));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userToStore);
      initNotifications(userData.journeyStage)
        .then(r => { if (!r.success) console.warn('[FCM] register init:', r.reason); })
        .catch(err => console.error('[FCM] register init error:', err.message));
      return { success: true };
    } catch (err) {
      if (!err.response) {
        return { success: false, message: 'Unable to reach TinySteps servers. Please check your connection.' };
      }
      return { success: false, message: err.response.data?.message || 'Registration failed. Please try again.' };
    }
  };

  const login = async (email, password) => {
    try {
      setError(null);
      const res = await axios.post(`${API_URL}/auth/login`, { email, password });
      const { token, ...userData } = res.data;
      // Returning users have completed onboarding
      const userToStore = { ...userData, onboardingComplete: true };
      localStorage.setItem('tinysteps_token', token);
      localStorage.setItem('tinysteps_user', JSON.stringify(userToStore));
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(userToStore);
      initNotifications(userData.journeyStage)
        .then(r => { if (!r.success) console.warn('[FCM] login init:', r.reason); })
        .catch(err => console.error('[FCM] login init error:', err.message));
      return { success: true };
    } catch (err) {
      if (!err.response) {
        return { success: false, code: 'NO_CONNECTION', message: 'Unable to reach TinySteps servers. Please check your connection.' };
      }
      return {
        success: false,
        code: err.response.data?.code || 'ERROR',
        message: err.response.data?.message || 'Something went wrong. Please try again.',
      };
    }
  };

  const markOnboardingComplete = () => {
    const updated = { ...user, onboardingComplete: true };
    localStorage.setItem('tinysteps_user', JSON.stringify(updated));
    setUser(updated);
  };

  const logout = () => {
    localStorage.removeItem('tinysteps_token');
    localStorage.removeItem('tinysteps_user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const updateUser = async (updates) => {
    try {
      const res = await axios.put(`${API_URL}/auth/profile`, updates);
      const updatedUser = res.data;
      localStorage.setItem('tinysteps_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      return { success: true };
    } catch (err) {
      return { success: false, message: err.response?.data?.message || 'Update failed' };
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, register, login, logout, updateUser, setUser, markOnboardingComplete }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export default AuthContext;
